import React, { useContext, useRef, useState } from 'react';
import { Card, CardContent } from '../../../lib/card';
import {
  CheckSquare,
  SquareLibrary,
  ClipboardList,
  Folder,
  FileSearch,
  GitBranch
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ScrollDownArrow } from '../shared/scrollDownArrow';
import { ProjectContext } from '../../../context/projectContext';
import { DOCTYPE, Projects } from '../../../lib/models/interfaces';
import { Box, Button, Modal, SpaceBetween } from '@cloudscape-design/components';

type DeviationProjects = {
  policy: Projects[];
  contract: Projects[];
};

type DeviationModalStep = 'action' | 'contract-projects' | 'policy-projects';

const emptyDeviationProjects: DeviationProjects = {
  policy: [],
  contract: []
};

export const LandingPageNew = () => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const { mainProjects } = useContext(ProjectContext);
  const [currentProjects, setCurrentProjects] =
    useState<DeviationProjects>(emptyDeviationProjects);

  // state
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [deviationModalStep, setDeviationModalStep] =
    useState<DeviationModalStep>('action');

  const closeDeviationModal = () => {
    setModalVisible(false);
    setDeviationModalStep('action');
  };

  const openExistingProject = (projectId: string) => {
    closeDeviationModal();
    navigate(`/app/projects/${projectId}/folder/`);
  };

  const openNewContractProject = () => {
    closeDeviationModal();
    navigate('/app/projects?showNew=true&type=contract');
  };

  const openNewPolicyProject = () => {
    closeDeviationModal();
    navigate('/app/projects?showNew=true&type=policy_document');
  };

  const deviationNavigation = () => {
    const reducedProjects = mainProjects.reduce(
      (acc, curr) => {
        if (curr.projectType === DOCTYPE.policy_document) {
          acc.policy.push(curr);
        }
        if (curr.projectType === DOCTYPE.contract) {
          acc.contract.push(curr);
        }
        return acc;
      },
      { policy: [], contract: [] } as {
        policy: Projects[];
        contract: Projects[];
      }
    );
    setCurrentProjects(reducedProjects);
    setDeviationModalStep('action');
    setModalVisible(true);
  };

  const sortedPolicyProjects = [...currentProjects.policy].sort((a, b) =>
    a.projectName.localeCompare(b.projectName)
  );
  const sortedContractProjects = [...currentProjects.contract].sort((a, b) =>
    a.projectName.localeCompare(b.projectName)
  );
  const hasPolicyProjects = sortedPolicyProjects.length > 0;
  const hasContractProjects = sortedContractProjects.length > 0;

  const renderProjectList = (
    projects: Projects[],
    emptyMessage: string,
    buttonText: string
  ) => {
    if (projects.length === 0) {
      return <Box color="text-body-secondary">{emptyMessage}</Box>;
    }

    return (
      <div className="grid grid-cols-1 gap-3">
        {projects.map((project) => (
          <button
            key={project._id}
            type="button"
            onClick={() => openExistingProject(project._id)}
            className="w-full rounded-lg border border-gray-200 bg-white p-4 text-left transition hover:border-blue-500 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-200"
          >
            <p className="text-base font-semibold text-gray-900">
              {project.projectName}
            </p>
            <p className="mt-1 text-sm text-gray-600">
              {project.projectDescription || 'No project description'}
            </p>
            <p className="mt-3 text-sm font-medium text-blue-700">
              {buttonText}
            </p>
          </button>
        ))}
      </div>
    );
  };

  const renderActionButton = (props: {
    title: string;
    description: string;
    buttonLabel: string;
    disabled?: boolean;
    onClick: () => void;
  }) => {
    return (
      <button
        type="button"
        disabled={props.disabled}
        onClick={props.onClick}
        className={`w-full rounded-lg border p-4 text-left transition focus:outline-none focus:ring-2 focus:ring-blue-200 ${
          props.disabled
            ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
            : 'border-gray-200 bg-white hover:border-blue-500 hover:bg-blue-50'
        }`}
      >
        <p className="text-base font-semibold">{props.title}</p>
        <p className="mt-2 text-sm">{props.description}</p>
        <p className="mt-3 text-sm font-medium text-blue-700">
          {props.buttonLabel}
        </p>
      </button>
    );
  };

  const renderDeviationModalContent = () => {
    switch (deviationModalStep) {
      case 'contract-projects':
        return (
          <SpaceBetween size="m">
            <Box color="text-body-secondary">
              Select the contract project you want to open for comparison.
            </Box>
            {renderProjectList(
              sortedContractProjects,
              'No contract projects are available yet.',
              'Open contract project'
            )}
            <Button variant="normal" onClick={openNewContractProject}>
              Create New Contract Project
            </Button>
          </SpaceBetween>
        );
      case 'policy-projects':
        return (
          <SpaceBetween size="m">
            <Box color="text-body-secondary">
              Select the policy project where you want to add a new policy.
            </Box>
            {renderProjectList(
              sortedPolicyProjects,
              'No policy projects are available yet.',
              'Open policy project'
            )}
            <Button variant="normal" onClick={openNewPolicyProject}>
              Create New Policy Project
            </Button>
          </SpaceBetween>
        );
      default:
        return (
          <SpaceBetween size="m">
            <div>
              <p className="text-base font-semibold text-gray-900">
                {hasPolicyProjects && hasContractProjects
                  ? 'What do you want to do?'
                  : 'Choose the next step for contract deviation.'}
              </p>
              <p className="mt-2 text-sm text-gray-600">
                Policy projects: {sortedPolicyProjects.length} | Contract
                projects: {sortedContractProjects.length}
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {renderActionButton({
                title: 'Compare A Contract',
                description:
                  'Choose an existing contract project and continue from there.',
                buttonLabel: hasContractProjects
                  ? 'Choose contract project'
                  : 'No contract projects yet',
                disabled: !hasContractProjects,
                onClick: () => setDeviationModalStep('contract-projects')
              })}
              {renderActionButton({
                title: 'Create A New Policy',
                description:
                  'Open one of your existing policy projects and add the new policy there.',
                buttonLabel: hasPolicyProjects
                  ? 'Choose policy project'
                  : 'No policy projects yet',
                disabled: !hasPolicyProjects,
                onClick: () => setDeviationModalStep('policy-projects')
              })}
              {renderActionButton({
                title: 'Create A New Policy Project',
                description:
                  'Start a new policy-document project before adding policies.',
                buttonLabel: 'Create policy project',
                onClick: openNewPolicyProject
              })}
            </div>
            {!hasContractProjects ? (
              <Box color="text-status-warning">
                You need at least one contract project before you can compare a
                contract.
              </Box>
            ) : null}
            {!hasPolicyProjects ? (
              <Box color="text-status-warning">
                You need at least one policy project before you can add a new
                policy to an existing project.
              </Box>
            ) : null}
          </SpaceBetween>
        );
    }
  };
  return (
    <div
      className="relative h-screen overflow-y-auto"
      ref={containerRef}
      style={{ scrollbarWidth: 'none' }}
    >
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-4 py-8">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-900 mb-6">
                AI Powered Compliance & Document Analysis.
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                Understand your documents, answer questionnaires and generate
                assessments - all in one platform.
              </p>
            </div>
          </div>
        </div>
        {/* Quick Start section */}
        <div className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">
              Quick Starts
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <Card
                className="bg-white"
                onClick={() =>
                  navigate('/app/projects?showNew=true&type=workspace')
                }
              >
                <CardContent className="p-6">
                  <div className="flex-col">
                    <div className="flex items-center gap-3 mb-4">
                      <SquareLibrary className="w-8 h-8 text-blue-600" />
                      <h3 className="text-xl font-semibold">
                        Create Document Project
                      </h3>
                    </div>
                    <p className="mb-4">
                      Upload your documents to analyze them with AI
                    </p>
                  </div>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start gap-2">
                      <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                      <span>Extract requirements, tasks, risks</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                      <span>Summarize multiple documents</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                      <span>Save AI findings for reuse</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card
                className="bg-white"
                onClick={() => navigate('/app/gapanalysis')}
              >
                <CardContent className="p-6">
                  <div className="flex-col">
                    <div className="flex items-center gap-3 mb-4">
                      <ClipboardList className="w-8 h-8 text-blue-600" />
                      <h3 className="text-xl font-semibold">
                        Run GAP Assessment
                      </h3>
                    </div>
                    <p className="mb-4">
                      Generate and autocomplete a standards based questionnaire
                      using your documents
                    </p>
                  </div>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start gap-2">
                      <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                      <span>Select A Compliance Standard</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                      <span>Choose your project documents</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                      <span>AI Completes the questionnaire</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card
                onClick={() =>
                  navigate(
                    '/app/projects?showNew=true&type=information_request'
                  )
                }
                className="bg-white"
              >
                <CardContent className="p-6">
                  <div className="flex-col">
                    <div className="flex items-center gap-3 mb-4">
                      <Folder className="w-8 h-8 text-blue-600" />
                      <h3 className="text-xl font-semibold">
                        Start Questionnaire Project
                      </h3>
                    </div>
                    <p className="mb-4">
                      Import a spreadsheet questionnaire and let AI help
                      generate accurate answers
                    </p>
                  </div>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start gap-2">
                      <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                      <span>Upload Excel questionnaires</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                      <span>Auto-generate draft responses</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                      <span>Review, refine and export</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
              <Card
                onClick={() => navigate('/app/search')}
                className="bg-white"
              >
                <CardContent className="p-6">
                  <div className="flex-col">
                    <div className="flex items-center gap-3 mb-4">
                      <FileSearch className="w-8 h-8 text-blue-600" />
                      <h3 className="text-xl font-semibold">
                        Search your documents
                      </h3>
                    </div>
                    <p className="mb-4">
                      Ask questions and explore information across your projects
                      or the global library
                    </p>
                  </div>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start gap-2">
                      <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                      <span>Ask targeted questions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                      <span>Compare documents</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                      <span>Discover related insights</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
              <Card onClick={deviationNavigation} className="bg-white">
                <CardContent className="p-6">
                  <div className="flex-col">
                    <div className="flex items-center gap-3 mb-4">
                      <GitBranch className="w-8 h-8 text-blue-600" />
                      <h3 className="text-xl font-semibold">
                        Contract Deviation
                      </h3>
                    </div>
                    <p className="mb-4">
                      Create Profiles and Compare Contracts
                    </p>
                  </div>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start gap-2">
                      <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                      <span>Create Profiles</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                      <span>Compare Contracts</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                      <span>Highlight Deviations</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Key Capabilities Section */}
        {/* <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-3xl font-bold text-center mb-12">
            What You Can Do with KORU
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <FileText className="w-8 h-8 text-blue-600" />
                  <h3 className="text-xl font-semibold">Analyze Documents</h3>
                </div>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                    <span>
                      Automatically identify key obligations and requirements
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                    <span>Extract and track important deadlines</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                    <span>Categorize information by type and priority</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <GitMerge className="w-8 h-8 text-blue-600" />
                  <h3 className="text-xl font-semibold">Connect Information</h3>
                </div>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                    <span>Link related requirements across documents</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                    <span>Maintain traceability as obligations evolve</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                    <span>
                      Create a searchable web of connected information
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Users className="w-8 h-8 text-blue-600" />
                  <h3 className="text-xl font-semibold">Collaborate & Share</h3>
                </div>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                    <span>Work together on document reviews</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                    <span>Share insights and maintain review history</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                    <span>Generate focused reports for stakeholders</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
          <div className="mt-12 text-center">
            <button
              className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 inline-flex items-center gap-2"
              onClick={() => {
                navigate('/app/projects');
              }}
            >
              Start Your Project <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div> */}

        {/* Knowledge Base Banner */}
        {/* <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Card className="bg-gray-50 border-gray-200">
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold mb-4">
                    Build Your Knowledge Base
                  </h2>
                  <p className="text-gray-600 mb-6">
                    Create a searchable library of document expertise that grows
                    with each project. Maintain institutional knowledge and
                    ensure consistency across your organization.
                  </p>
                  <button
                    className="bg-white text-blue-600 px-6 py-2 rounded-md border border-blue-600 hover:bg-blue-50"
                    onClick={() => {
                      navigate('/app/landing/knowledgebase');
                    }}
                  >
                    Learn More
                  </button>
                </div>
                <div className="flex-1 flex justify-center">
                  <BookOpen className="w-32 h-32 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div> */}
      </div>
      <Modal
        visible={modalVisible}
        onDismiss={closeDeviationModal}
        header={
          deviationModalStep === 'contract-projects'
            ? 'Select A Contract Project'
            : deviationModalStep === 'policy-projects'
              ? 'Select A Policy Project'
              : 'Contract Deviation'
        }
        footer={
          <SpaceBetween direction="horizontal" size="xs">
            {deviationModalStep !== 'action' ? (
              <Button onClick={() => setDeviationModalStep('action')}>
                Back
              </Button>
            ) : null}
            <Button variant="primary" onClick={closeDeviationModal}>
              Close
            </Button>
          </SpaceBetween>
        }
      >
        {renderDeviationModalContent()}
      </Modal>
      <ScrollDownArrow
        containerRef={containerRef as React.RefObject<HTMLElement>}
        bottomValue="12%"
      />
    </div>
  );
};
